import { Action, Middleware, configureStore } from '@reduxjs/toolkit';
import { Session } from 'application/Shared/types/session';
import { applyMiddleware } from 'redux';

import {
    addUserDataToHeap,
    addUserDataToPendo,
    addUserTrackingScripts
} from '../application/Shared/Utils/UserTracking';
import { addUserDataToAda } from '../application/Shared/Utils/ada';
import reducer from '../reducers/index';

/**
 * Interface for the STORE_INFO action
 */
interface StoreInfoAction extends Action {
    type: 'STORE_INFO';
    session?: Session;
}

/**
 * Middleware to handle user properties and tracking
 * @returns {Middleware} Redux middleware function
 */
const userPropertiesMiddleware: Middleware =
    () => (next) => (action: unknown) => {
        if ((action as Action).type === 'STORE_INFO') {
            const storeInfoAction = action as StoreInfoAction;
            const session = storeInfoAction?.session;

            // userTracking is enabled by default, so unless it's explicitly set to false, it's enabled.
            const userTrackingSetting = session?.tenant?.settings?.userTracking;
            const userTrackingEnabled = userTrackingSetting !== false;

            // We still want to add the base data to Ada, even if user tracking is disabled.
            addUserDataToAda(
                userTrackingEnabled ? session : undefined,
                !!session?.id // if session has an id, user is signed in
            );

            if (userTrackingEnabled) {
                // add tracking scripts if needed (heap, pendo)
                addUserTrackingScripts();

                // then add/update user data for heap and pendo
                addUserDataToHeap(session);
                addUserDataToPendo(session);
            }
        }
        next(action);
    };

/**
 * Sets up and configures the Redux store
 * @param {Object} preloadedState - The initial state to preload the store with
 * @returns {ReturnType<typeof configureStore>} Configured Redux store
 */
export const setupStore = (
    preloadedState = {}
): ReturnType<typeof configureStore> =>
    configureStore({
        devTools: process.env.NODE_ENV !== 'production',
        enhancers: (getDefaultEnhancers) =>
            getDefaultEnhancers().concat(
                applyMiddleware(userPropertiesMiddleware)
            ),
        preloadedState,
        reducer
    });
